import React, { useState } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import TripComponentForm from './TripComponentForm';
import ComponentTypeSelector from './ComponentTypeSelector';
import TripChauffeuredServiceBooking from './TripChauffeuredServiceBooking';
import BundleComponentForm from './BundleComponentForm';

const AddComponentModal = ({ tripId, onClose, onSuccess, tripExecutives, allExecutives = [], meetingExecutives, allContacts = [], tripComponents = [], initialType = null, initialComponentData = null }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [showSelector, setShowSelector] = useState(!initialType);
  const [selectedType, setSelectedType] = useState(initialType);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setShowSelector(false);
  };

  // Check if the selected type should use a specialized flow
  const useFullBookingFlow = selectedType === 'chauffeur_service';
  const useBundleFlow = selectedType === 'bundle';

  const handleBackToSelector = () => {
    setShowSelector(true);
    setSelectedType(null);
  };

  const handleComponentCreate = async (componentData) => {
    setIsCreating(true);
    setCreateError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const url = `${apiBase}/trips/${tripId}/components`;
      await axios.post(url, componentData, { headers });
      onSuccess && onSuccess();
    } catch (err) {
      let errorMessage = 'Failed to create component';
      if (err.response?.data?.detail) errorMessage += `: ${err.response.data.detail}`;
      else if (err.response?.data?.message) errorMessage += `: ${err.response.data.message}`;
      else if (err.message) errorMessage += `: ${err.message}`;
      else errorMessage += `: ${JSON.stringify(err)}`;
      setCreateError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-overlay/40 flex items-center justify-center z-[70]">
      <div className={`bg-ui-surface rounded-lg p-6 w-full max-h-[90vh] overflow-y-auto ${useFullBookingFlow || useBundleFlow ? 'max-w-4xl' : 'max-w-5xl'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {!showSelector && (
              <button onClick={handleBackToSelector} className="text-ui-ink-3 hover:text-ui-ink text-sm">← Back</button>
            )}
            <h2 className="text-xl font-semibold">Add Component</h2>
          </div>
          <button onClick={onClose} className="text-ui-muted hover:text-ui-ink-2">✕</button>
        </div>

        {createError && (
          <div className="mb-4 p-3 bg-ui-red-bg border border-ui-red-line text-ui-red-fg rounded">{createError}</div>
        )}

        {showSelector ? (
          <ComponentTypeSelector onSelect={handleTypeSelect} onCancel={onClose} />
        ) : useBundleFlow ? (
          <BundleComponentForm
            tripId={tripId}
            tripComponents={tripComponents}
            onSuccess={() => { onSuccess && onSuccess(); }}
            onCancel={handleBackToSelector}
          />
        ) : useFullBookingFlow ? (
          <TripChauffeuredServiceBooking
            tripId={tripId}
            tripExecutives={tripExecutives}
            allExecutives={allExecutives}
            allContacts={allContacts}
            onSuccess={() => {
              onSuccess && onSuccess();
            }}
            onCancel={handleBackToSelector}
          />
        ) : (
          <TripComponentForm
            tripId={tripId}
            componentType={selectedType}
            onSubmit={handleComponentCreate}
            onCancel={onClose}
            isSubmitting={isCreating}
            submitButtonText="Add Component"
            tripExecutives={tripExecutives}
            meetingExecutives={meetingExecutives}
            allContacts={allContacts}
            initialComponentData={initialComponentData}
          />
        )}
      </div>
    </div>
  );
};

export default AddComponentModal;